/* WalletActivityPanel — shared by Buyer Payments (Activity tab) and
   Seller Payouts (Activity tab).

   Three blocks, in this order:
     1. Smart filter row — free-text search, type/direction selects,
        amount range, date range. All filters compose; URL stays clean.
     2. Transaction list — filtered server-side via /wallet/transactions,
        with refresh + CSV export of the current filtered view.
     3. Monthly statements grid — list of months that have at least one
        transaction, each with PDF + CSV download buttons.

   The panel is intentionally presentation-only; it owns no business
   logic outside the filter UX. Drop into any payments-style page. */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { money } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import {
  Search, Filter, X, RefreshCw, Download, FileText, FileSpreadsheet,
  ArrowDownLeft, ArrowUpRight, Calendar, ChevronLeft, ChevronRight,
  ExternalLink,
} from "lucide-react";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const TYPE_OPTIONS = [
  { v: "",                  l: "All types" },
  { v: "topup",             l: "Top-ups" },
  { v: "withdraw",          l: "Withdrawals" },
  { v: "payment",           l: "Invoice payments" },
  { v: "payment_received",  l: "Payments received" },
  { v: "prebook_deposit",   l: "Prebook deposits" },
  { v: "prebook_refund_in", l: "Prebook refunds in" },
  { v: "prebook_refund_out",l: "Prebook refunds out" },
  { v: "insurance_payout",  l: "Insurance payouts" },
];

const DIRECTION_OPTIONS = [
  { v: "",    l: "Any direction" },
  { v: "in",  l: "Money in (+)" },
  { v: "out", l: "Money out (−)" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

function buildQuery(f) {
  const p = new URLSearchParams();
  if (f.q)          p.set("q", f.q);
  if (f.type)       p.set("type", f.type);
  if (f.direction)  p.set("direction", f.direction);
  if (f.dateFrom)   p.set("date_from", f.dateFrom);
  if (f.dateTo)     p.set("date_to", f.dateTo);
  if (f.minAmount)  p.set("min_amount", String(f.minAmount));
  if (f.maxAmount)  p.set("max_amount", String(f.maxAmount));
  return p.toString();
}

export default function WalletActivityPanel({
  title = "Activity",
  subtitle = "Every dollar in & out — search, filter, and download monthly statements.",
  testIdPrefix = "activity",
  // "seller" → clicking the invoice chip routes to /seller/invoices?focus=…
  // "buyer"  → routes to /buyer/invoices?focus=…
  // omitted → no chip rendered (chip only appears when caller opts in)
  role = null,
  extraItems = null,   // optional: array of invoice/order rows to merge into the
                       // transaction list (used by Buyer Payments to surface
                       // invoice-paid events alongside wallet txns).
}) {
  const [filters, setFilters] = useState({
    q: "", type: "", direction: "",
    dateFrom: "", dateTo: "",
    minAmount: "", maxAmount: "",
  });
  const [committed, setCommitted] = useState(filters);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statements, setStatements] = useState([]);
  const [stLoading, setStLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  // ── Pagination state — keeps the activity list short so the
  //    Statements section is always reachable without a long scroll.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // ── Statement search — filter the monthly list by label.
  const [stmtQuery, setStmtQuery] = useState("");

  // Reset to page 1 whenever filters change or transactions reload.
  useEffect(() => { setPage(1); }, [committed, extraItems]);

  const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const clear = () => { const empty = { q: "", type: "", direction: "", dateFrom: "", dateTo: "", minAmount: "", maxAmount: "" }; setFilters(empty); setCommitted(empty); };
  const apply = () => setCommitted(filters);

  const activeFilterCount = useMemo(() => {
    return Object.values(committed).filter(v => v !== "" && v !== null && v !== undefined).length;
  }, [committed]);

  // ── Fetch filtered transactions ────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const qs = buildQuery(committed);
        const { data } = await api.get(`/wallet/transactions${qs ? `?${qs}` : ""}`);
        if (alive) setTxns(data.transactions || []);
      } catch {
        if (alive) setTxns([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [committed]);

  // ── Fetch monthly statement index (once) ───────────────────────────────
  useEffect(() => {
    (async () => {
      setStLoading(true);
      try {
        const { data } = await api.get("/wallet/statements");
        setStatements(data.months || []);
      } catch {
        setStatements([]);
      } finally {
        setStLoading(false);
      }
    })();
  }, []);

  // Merge extra invoice/order events with wallet txns, sorted by date.
  const mergedTxns = useMemo(() => {
    if (!extraItems?.length) return txns;
    const xs = extraItems.map(x => ({ ...x, __extra: true }));
    return [...xs, ...txns].sort((a, b) =>
      new Date(b.created_at || b.date || 0) - new Date(a.created_at || a.date || 0)
    );
  }, [txns, extraItems]);

  // ── Paginated slice of the merged list ─────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(mergedTxns.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * pageSize;
  const pageEnd = pageStart + pageSize;
  const pageTxns = mergedTxns.slice(pageStart, pageEnd);

  // ── Filtered monthly statements (by label / yyyy-mm) ───────────────────
  const filteredStatements = useMemo(() => {
    const q = stmtQuery.trim().toLowerCase();
    if (!q) return statements;
    return statements.filter(m => {
      const ym = `${m.year}-${String(m.month).padStart(2, "0")}`;
      return (m.label || "").toLowerCase().includes(q) || ym.includes(q);
    });
  }, [statements, stmtQuery]);

  const downloadStatement = async (year, month, fmt) => {
    const key = `${year}-${month}-${fmt}`;
    setDownloading(key);
    try {
      const r = await api.get(`/wallet/statements/${year}/${month}.${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `wallet-statement-${year}-${String(month).padStart(2, "0")}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${fmt.toUpperCase()} statement`);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Could not download ${fmt.toUpperCase()}`);
    } finally {
      setDownloading(null);
    }
  };

  const exportCurrentView = () => {
    // Server-streamed CSV — uses the same filters as the visible list,
    // returns Content-Disposition: attachment so the browser handles the
    // download natively (works reliably in prod, mobile, embedded
    // webviews, popup-blocked tabs, etc.).
    const qs = buildQuery(committed);
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
    const url = `${base}/api/wallet/transactions/export.csv${qs ? `?${qs}` : ""}`;
    // Open in a hidden anchor so the auth cookie is sent.
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Preparing your CSV export…");
  };

  // ── Date preset helpers ────────────────────────────────────────────────
  const todayIso = () => new Date().toISOString().slice(0, 10);
  const daysAgoIso = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
  const startOfYearIso = () => `${new Date().getFullYear()}-01-01`;
  const startOfMonthIso = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
  };

  const applyPreset = (key) => {
    let next = { ...filters };
    if (key === "30d")  next = { ...next, dateFrom: daysAgoIso(30),  dateTo: todayIso() };
    if (key === "90d")  next = { ...next, dateFrom: daysAgoIso(90),  dateTo: todayIso() };
    if (key === "mtd")  next = { ...next, dateFrom: startOfMonthIso(), dateTo: todayIso() };
    if (key === "ytd")  next = { ...next, dateFrom: startOfYearIso(),  dateTo: todayIso() };
    if (key === "12m")  next = { ...next, dateFrom: daysAgoIso(365), dateTo: todayIso() };
    if (key === "clear") next = { ...next, dateFrom: "", dateTo: "" };
    setFilters(next);
    setCommitted(next);
  };

  return (
    <div data-testid={`${testIdPrefix}-panel`} className="space-y-7">
      <div>
        <div className="text-xs mono uppercase tracking-[0.2em] text-foreground/50">Activity ledger</div>
        <h2 className="display text-3xl md:text-4xl tracking-[-0.02em] leading-tight mt-2">{title}</h2>
        <div className="mt-2 text-sm text-foreground/55 max-w-xl">{subtitle}</div>
      </div>

      {/* ── Smart filter row ─────────────────────────────────────────── */}
      <div className="rounded-2xl bg-white border border-border p-4 md:p-5 space-y-3" data-testid={`${testIdPrefix}-filters`}>
        {/* Date presets — one-tap scoping for the most common ranges. */}
        <div className="flex items-center gap-1.5 flex-wrap" data-testid={`${testIdPrefix}-presets`}>
          <span className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/45 mr-1">Quick range</span>
          {[
            { k: "30d",   l: "Last 30d" },
            { k: "90d",   l: "Last 90d" },
            { k: "mtd",   l: "Month-to-date" },
            { k: "ytd",   l: "Year-to-date" },
            { k: "12m",   l: "Last 12 months" },
            { k: "clear", l: "All time" },
          ].map(p => (
            <button
              key={p.k}
              onClick={() => applyPreset(p.k)}
              data-testid={`${testIdPrefix}-preset-${p.k}`}
              className="text-[11px] rounded-full border border-foreground/15 bg-secondary/40 px-3 py-1 hover:border-foreground/40 hover:bg-foreground/10 transition-colors"
            >
              {p.l}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-12 gap-2.5">
          <div className="md:col-span-5 relative">
            <Search className="w-3.5 h-3.5 text-foreground/40 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"/>
            <Input
              value={filters.q}
              onChange={e => set("q", e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") apply(); }}
              placeholder="Search ref, memo, type, method…"
              data-testid={`${testIdPrefix}-search`}
              className="h-10 pl-8 bg-secondary/40 border-border"
            />
          </div>
          <select
            value={filters.type} onChange={e => set("type", e.target.value)}
            data-testid={`${testIdPrefix}-type`}
            className="md:col-span-3 h-10 w-full rounded-md border border-border bg-secondary/40 px-3 text-sm"
          >
            {TYPE_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
          <select
            value={filters.direction} onChange={e => set("direction", e.target.value)}
            data-testid={`${testIdPrefix}-direction`}
            className="md:col-span-2 h-10 w-full rounded-md border border-border bg-secondary/40 px-3 text-sm"
          >
            {DIRECTION_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
          <Button
            onClick={apply}
            data-testid={`${testIdPrefix}-apply`}
            className="md:col-span-2 h-10 rounded-full bg-foreground text-background hover:bg-foreground/90"
          >
            <Filter className="w-3.5 h-3.5 mr-1.5"/>Apply
          </Button>
        </div>

        <div className="grid md:grid-cols-12 gap-2.5">
          <div className="md:col-span-3">
            <label className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50">Min amount</label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40 text-sm">$</span>
              <Input
                type="number" min={0} step="0.01"
                value={filters.minAmount}
                onChange={e => set("minAmount", e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") apply(); }}
                placeholder="0"
                data-testid={`${testIdPrefix}-min`}
                className="h-9 pl-6 bg-secondary/40 border-border"
              />
            </div>
          </div>
          <div className="md:col-span-3">
            <label className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50">Max amount</label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40 text-sm">$</span>
              <Input
                type="number" min={0} step="0.01"
                value={filters.maxAmount}
                onChange={e => set("maxAmount", e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") apply(); }}
                placeholder="∞"
                data-testid={`${testIdPrefix}-max`}
                className="h-9 pl-6 bg-secondary/40 border-border"
              />
            </div>
          </div>
          <div className="md:col-span-3">
            <label className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50">From date</label>
            <Input
              type="date" value={filters.dateFrom}
              onChange={e => set("dateFrom", e.target.value)}
              data-testid={`${testIdPrefix}-from`}
              className="h-9 mt-1 bg-secondary/40 border-border font-mono"
            />
          </div>
          <div className="md:col-span-3">
            <label className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50">To date</label>
            <Input
              type="date" value={filters.dateTo}
              onChange={e => set("dateTo", e.target.value)}
              data-testid={`${testIdPrefix}-to`}
              className="h-9 mt-1 bg-secondary/40 border-border font-mono"
            />
          </div>
        </div>

        <div className="flex items-center justify-between pt-1">
          <div className="text-[11px] text-foreground/55">
            {activeFilterCount === 0
              ? <span>No filters applied — showing the last 500 transactions.</span>
              : <span><strong>{activeFilterCount}</strong> filter{activeFilterCount === 1 ? "" : "s"} active · <strong>{mergedTxns.length}</strong> match{mergedTxns.length === 1 ? "" : "es"}</span>}
          </div>
          {activeFilterCount > 0 && (
            <button
              onClick={clear}
              data-testid={`${testIdPrefix}-clear`}
              className="text-[11px] inline-flex items-center text-foreground/60 hover:text-foreground"
            >
              <X className="w-3 h-3 mr-1"/>Clear all
            </button>
          )}
        </div>
      </div>

      {/* ── Transaction list ─────────────────────────────────────────── */}
      <div className="rounded-2xl bg-white border border-border">
        <div className="px-5 md:px-6 py-4 border-b border-border flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">Transactions</div>
            <h3 className="display text-lg mt-1">{loading ? "Loading…" : `${mergedTxns.length} result${mergedTxns.length === 1 ? "" : "s"}`}</h3>
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost" size="sm"
              onClick={() => setCommitted({ ...committed })}
              data-testid={`${testIdPrefix}-refresh`}
              className="h-9 rounded-full"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5"/>Refresh
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={exportCurrentView} disabled={!mergedTxns.length}
              data-testid={`${testIdPrefix}-export-view`}
              className="h-9 rounded-full"
              title="Download every transaction matching the filters above as CSV"
            >
              <Download className="w-3.5 h-3.5 mr-1.5"/>Export CSV
            </Button>
          </div>
        </div>
        {loading ? (
          <div className="p-12 text-center text-sm text-foreground/55">Loading transactions…</div>
        ) : mergedTxns.length === 0 ? (
          <div className="p-12 text-center text-sm text-foreground/55">
            No transactions match your filters.
          </div>
        ) : (
          <>
            <div className="divide-y divide-border">
              {pageTxns.map((tx, i) => <TxnRow key={tx.id || tx.ref || `${pageStart + i}`} tx={tx} idx={pageStart + i} testIdPrefix={testIdPrefix} role={role}/>)}
            </div>
            {/* ── Pager — keeps activity short so Statements stays in reach */}
            <div
              className="px-5 md:px-6 py-3 border-t border-border flex items-center justify-between flex-wrap gap-3"
              data-testid={`${testIdPrefix}-pager`}
            >
              <div className="text-[11px] text-foreground/55 mono">
                Showing <strong>{pageStart + 1}</strong>–<strong>{Math.min(pageEnd, mergedTxns.length)}</strong> of <strong>{mergedTxns.length}</strong>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-[11px] text-foreground/55 mono">Rows</label>
                <select
                  value={pageSize}
                  onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                  data-testid={`${testIdPrefix}-page-size`}
                  className="h-8 rounded-md border border-border bg-secondary/40 px-2 text-xs"
                >
                  {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <Button
                  variant="ghost" size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  data-testid={`${testIdPrefix}-prev`}
                  className="h-8 rounded-full px-3"
                >
                  <ChevronLeft className="w-3.5 h-3.5 mr-1"/>Prev
                </Button>
                <div className="text-[11px] mono text-foreground/70 px-1">
                  Page <strong>{safePage}</strong> / {totalPages}
                </div>
                <Button
                  variant="ghost" size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  data-testid={`${testIdPrefix}-next`}
                  className="h-8 rounded-full px-3"
                >
                  Next<ChevronRight className="w-3.5 h-3.5 ml-1"/>
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Monthly statements ───────────────────────────────────────── */}
      <div className="rounded-2xl bg-white border border-border" data-testid={`${testIdPrefix}-statements`}>
        <div className="px-5 md:px-6 py-4 border-b border-border space-y-3">
          <div>
            <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">Statements</div>
            <h3 className="display text-lg mt-1">Monthly statements</h3>
            <p className="text-xs text-foreground/55 mt-1">Download a signed PDF or CSV for any month with wallet activity — drops straight into your AP/AR system.</p>
          </div>
          <div className="relative max-w-sm">
            <Search className="w-3.5 h-3.5 text-foreground/40 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"/>
            <Input
              value={stmtQuery}
              onChange={e => setStmtQuery(e.target.value)}
              placeholder="Search statements (e.g. ‘Jan 2026’, ‘2025-12’)"
              data-testid={`${testIdPrefix}-stmt-search`}
              className="h-9 pl-8 bg-secondary/40 border-border"
            />
            {stmtQuery && (
              <button
                onClick={() => setStmtQuery("")}
                data-testid={`${testIdPrefix}-stmt-search-clear`}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-foreground/40 hover:text-foreground"
                aria-label="Clear statement search"
              >
                <X className="w-3.5 h-3.5"/>
              </button>
            )}
          </div>
        </div>
        {stLoading ? (
          <div className="p-10 text-center text-sm text-foreground/55">Loading statements…</div>
        ) : statements.length === 0 ? (
          <div className="p-10 text-center text-sm text-foreground/55">No statements yet — they appear after your first wallet activity.</div>
        ) : filteredStatements.length === 0 ? (
          <div className="p-10 text-center text-sm text-foreground/55" data-testid={`${testIdPrefix}-stmt-empty`}>
            No statements match “{stmtQuery}”.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filteredStatements.map(m => (
              <div key={`${m.year}-${m.month}`}
                className="px-5 md:px-6 py-4 flex items-center gap-4 flex-wrap"
                data-testid={`${testIdPrefix}-month-${m.year}-${m.month}`}
              >
                <div className="w-10 h-10 rounded-xl bg-secondary grid place-items-center shrink-0">
                  <Calendar className="w-4 h-4 text-foreground/55"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{m.label}</div>
                  <div className="text-[11px] text-foreground/55 mono mt-0.5">
                    {m.tx_count} txn{m.tx_count === 1 ? "" : "s"} · +{money(m.in)} in · −{money(m.out)} out · net {m.net >= 0 ? "+" : "−"}{money(Math.abs(m.net))}
                  </div>
                </div>
                <Button
                  variant="outline" size="sm"
                  onClick={() => downloadStatement(m.year, m.month, "pdf")}
                  disabled={downloading === `${m.year}-${m.month}-pdf`}
                  data-testid={`${testIdPrefix}-dl-pdf-${m.year}-${m.month}`}
                  className="h-9 rounded-full"
                >
                  <FileText className="w-3.5 h-3.5 mr-1.5"/>{downloading === `${m.year}-${m.month}-pdf` ? "…" : "PDF"}
                </Button>
                <Button
                  variant="ghost" size="sm"
                  onClick={() => downloadStatement(m.year, m.month, "csv")}
                  disabled={downloading === `${m.year}-${m.month}-csv`}
                  data-testid={`${testIdPrefix}-dl-csv-${m.year}-${m.month}`}
                  className="h-9 rounded-full"
                >
                  <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5"/>{downloading === `${m.year}-${m.month}-csv` ? "…" : "CSV"}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TxnRow({ tx, idx, testIdPrefix, role }) {
  const nav = useNavigate();
  const amount = Number(tx.amount ?? 0);
  const isIn = amount > 0;
  const label = (tx.title || tx.type || "Transaction").replace(/_/g, " ");
  const sub = [
    tx.method && String(tx.method).toUpperCase(),
    tx.ref,
    tx.memo || tx.sub,
  ].filter(Boolean).join(" · ");
  // Light-touch cross-link: surface the invoice this txn relates to as a
  // small clickable chip → jumps to the Invoices list with that row
  // pre-focused. Invoice info may live at the top level OR inside the
  // wallet_txn's metadata blob (different code paths write different
  // shapes — see backend `_normalise_txn`).
  const invoiceId   = tx.invoice_id   || tx.metadata?.invoice_id   || null;
  const invoiceCode = tx.invoice_code || tx.metadata?.invoice_code || null;
  const showChip = role && invoiceId && invoiceCode;
  const jumpToInvoice = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const base = role === "seller" ? "/seller/invoices" : "/buyer/invoices";
    nav(`${base}?focus=${invoiceId}`);
  };
  return (
    <div className="px-5 md:px-6 py-3.5 flex items-center justify-between gap-3"
      data-testid={`${testIdPrefix}-tx-${idx}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-9 h-9 rounded-xl grid place-items-center shrink-0 ${
          isIn ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
        }`}>
          {isIn ? <ArrowDownLeft className="w-4 h-4"/> : <ArrowUpRight className="w-4 h-4"/>}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium capitalize truncate flex items-center gap-2">
            <span className="truncate">{label}</span>
            {showChip && (
              <button
                onClick={jumpToInvoice}
                title={`Jump to ${invoiceCode} in Invoices`}
                data-testid={`${testIdPrefix}-tx-${idx}-invoice-chip`}
                className="inline-flex items-center gap-1 mono text-[10.5px] normal-case font-normal px-2 py-0.5 rounded-md border border-foreground/15 text-foreground/65 hover:bg-foreground hover:text-background hover:border-foreground transition-colors shrink-0"
              >
                <FileText className="w-2.5 h-2.5"/>
                {invoiceCode}
                <ExternalLink className="w-2.5 h-2.5 opacity-60"/>
              </button>
            )}
          </div>
          <div className="text-[11px] text-foreground/50 mono truncate">{sub || "—"}</div>
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className={`display text-base ${isIn ? "text-emerald-700" : "text-foreground"}`}>
          {isIn ? "+" : ""}{money(amount)}
        </div>
        <div className="text-[10px] text-foreground/45 mono mt-0.5">{fmtDate(tx.created_at || tx.date)}</div>
      </div>
    </div>
  );
}
